import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useIntl } from 'react-intl';
import { getAvatarStyle } from '../../../../lib/s3';

const UPDATE_USER_MUTATION = gql`
  mutation UpdateCurrentUser($profile_picture: String) {
    updateCurrentUser(profile_picture: $profile_picture)
  }
`;

const AvatarSelectionModal = ({ user, avatars = [], onClose, onSave, onError, companySettings = {} }) => {
  const intl = useIntl();
  const lastSubmittedFilenameRef = useRef(null);
  // Handle avatar IDs - for new system they come as 'avatar_001', for legacy as 'rj-1'
  const avatarIds = avatars.length > 0
    ? avatars.map(a => a.replace('-rectangle.png', '').replace('.png', '')) // Remove extension if present
    : ['rj-1', 'rj-2', 'rj-3', 'rj-4', 'rj-5', 'rj-6']; // Legacy fallback

  // Detect if we're using the new avatar system based on avatar naming pattern
  const hasNewStyleAvatars = avatarIds.length > 0 && avatarIds[0].match(/^avatar_\d{3}$/);

  // Extract the avatar ID from the user's current profile_picture
  const getCurrentAvatar = () => {
    if (!user.profile_picture) return avatarIds[0];

    // Extract just the filename if it's a full path
    const filenameOnly = user.profile_picture.split('/').pop();

    // Check for new-style avatar (avatar_XXX.png)
    const newMatch = filenameOnly.match(/^(avatar_\d{3})\.png$/);
    if (newMatch) {
      return newMatch[1];
    }

    // Check for legacy avatar (xxx-rectangle.png)
    const legacyMatch = filenameOnly.match(/^(.+?)-rectangle\.png$/);
    if (legacyMatch) {
      return legacyMatch[1];
    }

    // Fallback to first avatar
    return avatarIds[0];
  };

  const [selectedAvatar, setSelectedAvatar] = useState(getCurrentAvatar());

  const [updateUser, { loading }] = useMutation(UPDATE_USER_MUTATION, {
    onCompleted: () => onSave('Avatar updated successfully', lastSubmittedFilenameRef.current),
    onError: (error) => onError(error),
    refetchQueries: ['GetCurrentUserProfile'],
    awaitRefetchQueries: true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Detect if selected avatar is new-style based on naming pattern
    const isNewStyleAvatar = selectedAvatar.match(/^avatar_\d{3}$/);
    // Send the avatar filename with appropriate suffix
    const filename = isNewStyleAvatar
      ? `${selectedAvatar}.png`  // New system: avatar_001.png
      : `${selectedAvatar}-rectangle.png`;  // Legacy: rj-1-rectangle.png
    lastSubmittedFilenameRef.current = filename;
    updateUser({ variables: { profile_picture: filename } });
  };

  const handleCancel = () => {
    setSelectedAvatar(getCurrentAvatar());
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={handleCancel}>
      <div className="bg-succession-bg-secondary rounded-lg p-6 max-w-3xl w-full border border-succession-border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.profile_edit.modals.avatar_selection.title' })}</h2>
            <p className="text-succession-text-muted text-base mt-1">{intl.formatMessage({ id: 'succession.profile_edit.modals.avatar_selection.subtitle' })}</p>
          </div>
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-succession-bg-primary rounded-full transition-colors"
          >
            <svg className="w-6 h-6 text-succession-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="py-6 flex justify-center">
            <div className={`grid ${hasNewStyleAvatars ? 'grid-cols-5 gap-4' : 'grid-cols-3 gap-6'} w-fit max-h-96 overflow-y-auto`}>
                {avatarIds.map((avatarId, index) => {
                  // Detect new-style avatar from naming pattern
                  const isNewStyleAvatar = avatarId.match(/^avatar_\d{3}$/);

                  // Use appropriate filename format
                  const imageName = isNewStyleAvatar
                    ? `${avatarId}.png`
                    : `${avatarId}-rectangle.png`;

                  // Get avatar style (URL and background color) - automatically detects new vs legacy avatars
                  const avatarStyle = getAvatarStyle(imageName, companySettings);

                  return (
                    <button
                      key={avatarId}
                      type="button"
                      onClick={() => setSelectedAvatar(avatarId)}
                      className={`relative rounded-lg border-4 transition-all hover:scale-105 flex ${
                        isNewStyleAvatar ? 'items-end justify-center' : 'items-center justify-center overflow-hidden'
                      } ${
                        selectedAvatar === avatarId
                          ? 'border-succession-primary shadow-lg'
                          : 'border-succession-border hover:border-succession-text-muted'
                      }`}
                      style={{
                        width: isNewStyleAvatar ? '80px' : '160px',
                        height: isNewStyleAvatar ? '80px' : '120px',
                        backgroundColor: avatarStyle.backgroundColor || 'white',
                        paddingLeft: isNewStyleAvatar ? '4px' : '0',
                        paddingRight: isNewStyleAvatar ? '4px' : '0',
                        paddingTop: isNewStyleAvatar ? '4px' : '0',
                        paddingBottom: '0',
                      }}
                    >
                      <img
                        src={avatarStyle.url}
                        alt={intl.formatMessage({ id: 'succession.profile_edit.modals.avatar_selection.avatar_alt' }, { number: index + 1 })}
                        className={isNewStyleAvatar ? 'max-w-full max-h-full object-contain' : 'w-full h-full object-cover'}
                        style={{}}
                        onError={(e) => {
                          e.target.style.backgroundColor = '#cbd5e1';
                        }}
                      />
                      {selectedAvatar === avatarId && (
                        <div className="absolute top-1 right-1 bg-succession-primary text-white rounded-full p-0.5">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                })}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-succession-border">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
            >
              {intl.formatMessage({ id: 'succession.profile_edit.common.cancel' })}
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 rounded-lg text-white transition-colors ${
                loading
                  ? 'bg-succession-primary opacity-50 cursor-not-allowed'
                  : 'bg-succession-primary hover:opacity-90'
              }`}
            >
              {loading ? intl.formatMessage({ id: 'succession.profile_edit.common.saving' }) : intl.formatMessage({ id: 'succession.profile_edit.modals.avatar_selection.save_avatar' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

AvatarSelectionModal.propTypes = {
  user: PropTypes.object.isRequired,
  avatars: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  companySettings: PropTypes.shape({
    new_avatars_enabled: PropTypes.bool,
    avatar_directory: PropTypes.string,
    avatar_background_color: PropTypes.string,
    primary_color: PropTypes.string,
  }),
};

export default AvatarSelectionModal;
